'use client'
import Typography from 'src/components/design-system/Typography'
import { JournalArticleItem } from 'src/components/items/JournalArticleItem'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'
import useJournalDomainRoutes from 'src/components/journals/domain/useJournalDomainRoutes'
import { buildJournalArticleUrl } from 'src/_helpers/journals/buildJournalArticleUrl'
import { getPreferredTranslation } from 'src/_helpers/getPreferredTranslation'

const formatDate = (rawDate) => {
  if (!rawDate) return null

  const date = new Date(rawDate)
  if (!Number.isNaN(date.getTime())) {
    const day = String(date.getDate()).padStart(2, '0')
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const year = date.getFullYear()
    return `${day}.${month}.${year}`
  }

  if (typeof rawDate === 'string') {
    const normalized = rawDate.replace(/\//g, '.').trim()
    if (/^\d{2}\.\d{2}\.\d{4}$/.test(normalized)) {
      return normalized
    }
    return normalized
  }

  return null
}

export default function Articles({ items, locale, transCode, id = undefined, routeContext = 'main' }) {
  const translateEntry = useTranslateEntry()
  const journalRoutes = useJournalDomainRoutes()
  const isDomainJournal = routeContext === 'journal-domain'
  if (!items || items.length === 0) {
    return null
  }
  return (
    <section className="my-64 d:my-80 flex flex-col gap-y-32 d:pr-40" id={id}>
      <Typography variant="h2-caps-bold" component="h2" color="text-basic-black">
        {translateEntry(transCode)}
      </Typography>
      <ul
        className={`grid gap-x-42 gap-y-24 t:grid-cols-2 d:grid-cols-3 border-y py-16 ${
          isDomainJournal ? 'border-y-basic-gray7' : 'border-y-dark-blue-dark-blue6'
        }`}
      >
        {items.map((item, i) => {
          const article = item

          const articleTranslation = getPreferredTranslation(article, locale, { preferredFields: ['title'] }) || {}
          const articleTitle = articleTranslation?.title || ''
          const date = formatDate(article?.publishedAt)
          const url = buildJournalArticleUrl(article, locale, routeContext, journalRoutes) || '#'

          const tags = Array.isArray(articleTranslation?.tags)
            ? articleTranslation.tags.map((label) => {
                const normalized = String(label).toLowerCase()
                const type = normalized.includes('nowość') || normalized.includes('nowosc') ? 'new' : 'default'
                return { label, type }
              })
            : []

          const authors = (article.author || article.authors || []).map((a) => a?.fullName).filter(Boolean)

          return (
            <li
              key={i}
              className={`relative after:absolute after:-right-21 after:top-0 after:hidden after:h-full after:w-1 d:after:block d:last:after:hidden ${
                isDomainJournal ? 'after:bg-basic-gray7' : 'after:bg-dark-blue-dark-blue6'
              }`}
            >
              <JournalArticleItem
                date={date}
                title={articleTitle}
                url={url}
                tags={[
                  article.novelty && { label: translateEntry('T_NOVELTY'), type: 'new' },
                  //article.openAccess && { label: translateEntry('T_OPEN_ACCESS'), type: '' },
                  ...tags,
                ].filter(Boolean)}
                author={authors.join(', ')}
                variant={isDomainJournal ? 'journal-domain' : ''}
              />
            </li>
          )
        })}
      </ul>
    </section>
  )
}
